import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO, { SEO_DATA } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, X, ArrowRight, UserCheck, RefreshCw } from 'lucide-react';
import { PACKAGES, APPLICATION_SUPPORT } from '../data/pricing';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cb900e0f-c443-41f6-9df3-17a2db93c215/artifacts/qd501ira_D70BB6EE-7344-46BE-86BC-6C6D197A54F6.png";

export default function PricingPage() {
  const navigate = useNavigate();

  const comparisonFeatures = [
    { name: 'ATS-friendly formatting', resume_refresh: true, resume_rewrite: true, job_search_bundle: true },
    { name: 'Wording & grammar polish', resume_refresh: true, resume_rewrite: true, job_search_bundle: true },
    { name: 'Full resume rewrite', resume_refresh: false, resume_rewrite: true, job_search_bundle: true },
    { name: 'Achievement-focused bullets', resume_refresh: false, resume_rewrite: true, job_search_bundle: true },
    { name: 'Keyword alignment', resume_refresh: false, resume_rewrite: true, job_search_bundle: true },
    { name: 'Cover letter draft', resume_refresh: false, resume_rewrite: false, job_search_bundle: true },
    { name: 'LinkedIn headline + About', resume_refresh: false, resume_rewrite: false, job_search_bundle: true },
    { name: 'Revision rounds', resume_refresh: '1', resume_rewrite: '2', job_search_bundle: '2' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...SEO_DATA.pricing} />
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">
              Scope Confirmed Before Invoicing
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Services & Pricing
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Every project starts with intake so we can review your materials and confirm the right scope before invoicing.
            </p>
          </div>
        </section>

        {/* Trust Message */}
        <section className="max-w-4xl mx-auto px-6 -mt-8 mb-12">
          <Card className="p-6 rounded-2xl bg-teal-50 border-teal-200">
            <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
              <UserCheck className="h-12 w-12 text-teal-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Every project is personally reviewed before an invoice is sent</h3>
                <p className="text-slate-600 text-sm">
                  You won't be charged until we've reviewed your materials and confirmed the right scope. Reasonable revisions within the agreed scope are always included.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* Service Cards */}
        <section className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
              Service Options
            </h2>
            <p className="text-slate-600">Starting prices shown. Final scope confirmed after review.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {PACKAGES.map((pkg) => (
              <Card 
                key={pkg.id}
                className={`relative p-8 rounded-3xl border-2 bg-white ${
                  pkg.id === 'job_search_bundle' ? 'border-teal-500 shadow-2xl' : 'border-slate-200'
                }`}
                data-testid={`pricing-card-${pkg.id}`}
              >
                {pkg.id === 'job_search_bundle' && (
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-teal-600 text-white">Best Value</Badge>
                  </div>
                )}
                
                <div className="logo-badge">
                  <img src={LOGO_URL} alt="" className="h-8 w-8 rounded-lg" />
                </div>

                <div className="pt-6 text-center">
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>{pkg.name}</h3>
                  <div className="flex items-baseline gap-1 justify-center mb-1">
                    <span className="text-sm text-slate-500">Starting at</span>
                  </div>
                  <div className="flex items-baseline gap-1 justify-center mb-4">
                    <span className="text-4xl font-bold text-slate-900">${pkg.price}</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-6">{pkg.description}</p>

                  <ul className="space-y-3 mb-8 text-left">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button 
                    onClick={() => navigate('/start')}
                    className={`w-full rounded-full py-6 ${
                      pkg.id === 'job_search_bundle' ? 'bg-teal-600 hover:bg-teal-700' : 'bg-slate-900 hover:bg-slate-800'
                    }`}
                    data-testid={`pricing-select-${pkg.id}`}
                  >
                    Start Intake
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {/* Application Support */}
          <Card className="p-8 rounded-3xl border-2 border-amber-400 bg-amber-50/50 mb-8" data-testid="pricing-application-support">
            <div className="flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-1">
                <Badge className="bg-amber-500 text-white mb-4">Structured Application Help</Badge>
                <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                  {APPLICATION_SUPPORT.name}
                </h3>
                <p className="text-slate-600 mb-4">
                  {APPLICATION_SUPPORT.description}
                </p>
                <ul className="grid grid-cols-2 gap-2 mb-6">
                  {APPLICATION_SUPPORT.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-amber-600" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center">
                <div className="text-sm text-slate-500 mb-1">Starting at</div>
                <div className="text-4xl font-bold text-slate-900 mb-4">${APPLICATION_SUPPORT.price}</div>
                <Button 
                  onClick={() => navigate('/start')}
                  className="bg-amber-500 hover:bg-amber-600 rounded-full px-8"
                  data-testid="pricing-select-application-support"
                >
                  Start Intake
                </Button>
              </div>
            </div>
          </Card>

          <p className="text-center text-slate-500 text-sm mb-16">
            Additional LinkedIn and cover letter support can also be added after intake if needed.
          </p>
        </section>

        {/* Comparison Table */}
        <section className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Compare Options
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-2xl shadow-sm border border-slate-200" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left p-4 font-semibold text-slate-900">Feature</th>
                  <th className="p-4 font-semibold text-slate-900">Resume Refresh<br/><span className="font-normal text-slate-500">from $59</span></th>
                  <th className="p-4 font-semibold text-slate-900">Resume Rewrite<br/><span className="font-normal text-slate-500">from $99</span></th>
                  <th className="p-4 font-semibold text-teal-700 bg-teal-50">Job Search Bundle<br/><span className="font-normal text-teal-600">from $149</span></th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((row, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="p-4 text-slate-700">{row.name}</td>
                    <td className="p-4 text-center">
                      {row.resume_refresh === true ? <Check className="h-5 w-5 text-teal-600 mx-auto" /> :
                       row.resume_refresh === false ? <X className="h-5 w-5 text-slate-300 mx-auto" /> :
                       <span className="text-slate-600">{row.resume_refresh}</span>}
                    </td>
                    <td className="p-4 text-center">
                      {row.resume_rewrite === true ? <Check className="h-5 w-5 text-teal-600 mx-auto" /> :
                       row.resume_rewrite === false ? <X className="h-5 w-5 text-slate-300 mx-auto" /> :
                       <span className="text-slate-600">{row.resume_rewrite}</span>}
                    </td>
                    <td className="p-4 text-center bg-teal-50/50">
                      {row.job_search_bundle === true ? <Check className="h-5 w-5 text-teal-600 mx-auto" /> :
                       row.job_search_bundle === false ? <X className="h-5 w-5 text-slate-300 mx-auto" /> :
                       <span className="text-slate-600">{row.job_search_bundle}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Revisions Note */}
        <section className="max-w-3xl mx-auto px-6 mt-12">
          <Card className="p-6 rounded-2xl bg-slate-100 border-0">
            <div className="flex items-start gap-4">
              <RefreshCw className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Revisions included within scope</h4>
                <p className="text-sm text-slate-600">
                  Every project includes reasonable revisions within the agreed scope. We work with you to refine the final result so it feels ready to use.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 mt-16 text-center">
          <Card className="p-8 rounded-3xl bg-slate-900 text-white">
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Ready to Get Started?
            </h3>
            <p className="text-slate-300 mb-6">
              Submit your intake and we'll review your materials personally before confirming the right scope and pricing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/start')}
                className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
                data-testid="pricing-main-cta"
              >
                Start Your Intake
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </Card>
        </section>
      </main>

      <Footer />

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200 md:hidden z-40">
        <Button 
          onClick={() => navigate('/start')}
          className="w-full bg-teal-600 hover:bg-teal-700 rounded-full py-6"
          data-testid="pricing-sticky-cta"
        >
          Start Your Intake
        </Button>
      </div>
    </div>
  );
}
